import React, { type FunctionComponent } from 'react'
import { css } from '@emotion/react'
import { borderRadius, colors, spacing, Text } from '@retailer-platform/shared-components'
import { MerchandisingFormSection } from 'domains/merchandising/components/form-section/MerchandisingFormSection'
import { type BannerContentTextValues } from 'domains/banners/types/banner.types'
import { useDashMessages } from 'intl/intl.hooks'

type FieldProps = {
  color: string
  backgroundColor?: string
  name: string
  value: string
}

const sectionContainer = css({
  display: 'flex',
  flexDirection: 'column',
})

const rowContainer = css({
  display: 'flex',
  marginBottom: spacing.X24,
  width: '100%',
})

const textContainer = css({
  width: '280px',
})

const colorBlockStyle = css({
  background: colors.GRAYSCALE.X70,
  borderRadius: borderRadius.X4,
  display: 'inline-block',
  height: '16px',
  marginRight: spacing.X8,
  width: '16px',
  verticalAlign: 'middle',
})

export const BannerDetailsContentText: FunctionComponent<BannerContentTextValues> = ({
  button,
  headingText,
  subheadingText,
  backgroundColor,
  textareaColor,
  secondaryText,
}) => {
  const messages = useDashMessages({
    colorLabel: 'bannersDomain.details.color.label',
    backgroundColorLabel: 'bannersDomain.details.backgroundColor.label',
    emptyValue: 'bannersDomain.field.empty.value',
    imageLabel: 'bannersDomain.details.content.field.image.label',
  })

  const fields: FieldProps[] = [
    {
      name: 'Heading',
      color: headingText.color || messages.emptyValue,
      value: headingText.text || messages.emptyValue,
    },
    {
      name: 'Subheading',
      color: subheadingText.color || messages.emptyValue,
      value: subheadingText.text || messages.emptyValue,
    },

    {
      name: 'Banner background',
      color: backgroundColor || messages.emptyValue,
      value: null,
    },

    {
      name: 'CTA',
      color: button.color || messages.emptyValue,
      value: button.label || messages.emptyValue,
      backgroundColor: button.backgroundColor || messages.emptyValue,
    },
  ]

  if (textareaColor) {
    fields.push({
      name: 'Text area color',
      color: textareaColor || messages.emptyValue,
      value: null,
    })
  }

  if (secondaryText) {
    fields.push({
      name: 'Secondary text',
      color: secondaryText.color || messages.emptyValue,
      value: secondaryText.text || messages.emptyValue,
    })
  }

  return (
    <MerchandisingFormSection titleId="bannersDomain.details.content.title">
      <div css={sectionContainer}>
        {fields.map(({ name, color, value, backgroundColor }) => (
          <div css={rowContainer} key={`banner-details-info-${name}`}>
            {value && (
              <div css={textContainer}>
                <Text weight="semibold">{name}</Text>
                <Text size="medium">{value}</Text>
              </div>
            )}
            <div>
              <Text weight="semibold">
                {name} {messages.colorLabel}
              </Text>
              <Text size="medium">
                <span css={[colorBlockStyle, { backgroundColor: color }]} />
                {color}
              </Text>
            </div>
            {backgroundColor && (
              <div>
                <Text weight="semibold">
                  {name} {messages.backgroundColorLabel}
                </Text>
                <Text size="medium">
                  <span css={[colorBlockStyle, { backgroundColor: backgroundColor }]} />
                  {backgroundColor}
                </Text>
              </div>
            )}
          </div>
        ))}
      </div>
    </MerchandisingFormSection>
  )
}
